import { FC, useEffect, useMemo, useCallback, useReducer, useRef } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GitProviderPicker } from "~/components/pipeline/New/ConnectGitPicker/GitProviderPicker";
import { RepositoryPicker } from "~/components/pipeline/New/ConnectGitPicker/RepositoryPicker";
import {
  isGitHubService,
  type SCMServiceInfo,
  type RepositoryInfo,
  type ManualProviderType,
  type RepositoryURLType,
  type ParsedRepositoryUrl,
  type RepositorySelectionEventDetail,
} from "~/components/pipeline/New/ConnectGitPicker/util/types";
import Icon from "~/components/shared/Icon";
import ToolbarButton, {
  ToolbarButtonGroup,
} from "~/components/build/Show/components/ToolbarButton";
import exampleToRepo, {
  Example,
} from "~/components/pipeline/New/ConnectGitPicker/util/exampleToRepo";

const URLType = {
  SSH: "ssh_url" as RepositoryURLType,
  HTTPS: "clone_url" as RepositoryURLType,
};

interface ComponentState {
  selectedServiceUuid: string | null;
  selectedRepository: RepositoryInfo | null;
  selectedRepositoryURLType: RepositoryURLType;
  selectedManualProvider: ManualProviderType;
  hasUserSelection: boolean;
  autoCreateWebhooks: boolean;
  repositoryPickerOpen: boolean;
}

type ComponentAction =
  | {
      type: "PROPS_CHANGED";
      providerRepository: RepositoryInfo | null;
      parsedRepositoryUrl?: ParsedRepositoryUrl | null;
    }
  | {
      type: "SERVICES_CHANGED";
      services: SCMServiceInfo[] | null;
      defaultServiceUuid?: string | null;
      disableAutoSelect?: boolean;
    }
  | {
      type: "USER_SELECTED_SERVICE";
      service: SCMServiceInfo | null;
      manualProviderType?: ManualProviderType;
    }
  | { type: "TOGGLE_REPOSITORY_PICKER"; serviceUuid?: string; open?: boolean }
  | { type: "USER_SELECTED_REPOSITORY"; repository: RepositoryInfo | null }
  | { type: "USER_AUTO_SELECTED_REPOSITORY"; repository: RepositoryInfo }
  | { type: "USER_CHANGED_URL_TYPE"; urlType: RepositoryURLType }
  | { type: "USER_CHANGED_MANUAL_PROVIDER"; provider: ManualProviderType }
  | { type: "USER_CHANGED_WEBHOOKS"; enabled: boolean };

function selectDefaultService(services: SCMServiceInfo[]): SCMServiceInfo | null {
  // If a service is marked as default it has the highest priority
  const defaultFlaggedService = services.find((svc) => svc.is_default);
  if (defaultFlaggedService) {
    return defaultFlaggedService;
  }

  // Otherwise we'll default to GitHub code access if it exists
  const githubServices = services.filter(isGitHubService);
  if (githubServices.length > 1) {
    const githubWithCodeAccess = githubServices.find((svc) => svc.has_code_access);
    if (githubWithCodeAccess) {
      return githubWithCodeAccess;
    }
  }

  // Or just grab the first service
  return services[0] || null;
}

function componentReducer(state: ComponentState, action: ComponentAction): ComponentState {
  switch (action.type) {
    case "PROPS_CHANGED": {
      return {
        ...state,
        selectedRepository: action.providerRepository || state.selectedRepository,
        selectedRepositoryURLType:
          action.parsedRepositoryUrl?.url_type || state.selectedRepositoryURLType,
        selectedManualProvider:
          action.parsedRepositoryUrl?.detected_provider || state.selectedManualProvider,
        hasUserSelection: action.providerRepository ? false : state.hasUserSelection,
      };
    }

    case "SERVICES_CHANGED": {
      if (!action.services?.length || state.hasUserSelection) {
        return state;
      }

      const defaultService = action.defaultServiceUuid
        ? action.services.find((svc) => svc.uuid === action.defaultServiceUuid)
        : selectDefaultService(action.services);

      if (!defaultService || state.selectedServiceUuid) {
        return state;
      }

      // Only auto-select repository if not disabled
      const defaultRepo = action.disableAutoSelect
        ? null
        : defaultService.repositories?.find((repo) => repo.is_default) ||
          defaultService.repositories?.[0];

      return {
        ...state,
        selectedServiceUuid: defaultService.uuid,
        selectedRepository: defaultRepo || state.selectedRepository,
      };
    }

    case "TOGGLE_REPOSITORY_PICKER": {
      return {
        ...state,
        selectedServiceUuid: action.serviceUuid || state.selectedServiceUuid,
        selectedRepository:
          action.serviceUuid && action.serviceUuid !== state.selectedServiceUuid
            ? null
            : state.selectedRepository,
        repositoryPickerOpen:
          typeof action.open !== "undefined" ? action.open : !state.repositoryPickerOpen,
      };
    }

    case "USER_SELECTED_SERVICE":
      return {
        ...state,
        selectedServiceUuid: action.service?.uuid || null,
        selectedRepository: null, // Reset repository when service changes
        selectedManualProvider: action.manualProviderType || "git",
        selectedRepositoryURLType: isGitHubService(action.service)
          ? action.service?.has_code_access
            ? URLType.HTTPS
            : URLType.SSH
          : state.selectedRepositoryURLType,
        hasUserSelection: true,
      };

    case "USER_SELECTED_REPOSITORY":
      return {
        ...state,
        selectedRepository: action.repository,
        hasUserSelection: true,
      };

    case "USER_AUTO_SELECTED_REPOSITORY":
      return {
        ...state,
        selectedRepository: action.repository,
        hasUserSelection: true,
      };

    case "USER_CHANGED_URL_TYPE":
      return {
        ...state,
        selectedRepositoryURLType: action.urlType,
      };

    case "USER_CHANGED_MANUAL_PROVIDER":
      return {
        ...state,
        selectedManualProvider: action.provider,
      };

    case "USER_CHANGED_WEBHOOKS":
      return {
        ...state,
        autoCreateWebhooks: action.enabled,
      };

    default:
      return state;
  }
}

interface Props {
  connectAccountURL?: string;
  connectGitHubAccountURL?: string;
  services?: Array<SCMServiceInfo> | null;
  defaultServiceUuid?: string | null;
  providerRepository?: RepositoryInfo | null;
  parsedRepositoryUrl?: ParsedRepositoryUrl | null;
  defaultUrlType?: RepositoryURLType;
  repositoryValue?: string;
  allowWebhooksCheckbox?: boolean;
  disableAutoSelect?: boolean;
  preselectedExample?: Example | null;
  hasPreselectedPublicTemplate?: boolean;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
    },
  },
});

const ConnectGitPicker: FC<Props> = ({
  connectAccountURL,
  connectGitHubAccountURL,
  services,
  defaultServiceUuid,
  providerRepository,
  preselectedExample,
  hasPreselectedPublicTemplate,
  parsedRepositoryUrl,
  defaultUrlType = URLType.SSH,
  repositoryValue,
  allowWebhooksCheckbox,
  disableAutoSelect = false,
}) => {
  // Initialize state
  const initialState: ComponentState = {
    selectedServiceUuid: preselectedExample ? "examples" : defaultServiceUuid || null,
    selectedRepository: preselectedExample
      ? exampleToRepo([preselectedExample])[0]
      : providerRepository || null,
    selectedRepositoryURLType: parsedRepositoryUrl?.url_type || defaultUrlType,
    selectedManualProvider: parsedRepositoryUrl?.detected_provider || "git",
    hasUserSelection: false,
    autoCreateWebhooks: true,
    repositoryPickerOpen: false,
  };

  const [state, dispatch] = useReducer(componentReducer, initialState);

  useEffect(() => {
    const form = document.querySelector<HTMLDivElement>("#project_form");
    if (form) {
      form.dataset.ready = "true";
    }
  }, []);

  const prefillFormFields = useCallback(
    (fields: { name: string; description: string; defaultBranch: string }) => {
      const name = document.querySelector<HTMLInputElement>(`[name="project[name]"]`);
      if (name) {
        name.value = fields.name;
        name.dataset.pipelineNameValidationAutoPopulatedValue = "true";
        name.dispatchEvent(new Event("input", { bubbles: true }));
      }

      const description = document.querySelector<HTMLInputElement>(`[name="project[description]"]`);
      if (description) {
        description.value = fields.description;
      }

      const defaultBranch = document.querySelector<HTMLInputElement>(
        `[name="project[default_branch]"]`,
      );
      if (defaultBranch) {
        defaultBranch.value = fields.defaultBranch;
      }
    },
    [],
  );

  // Handle prop changes
  useEffect(() => {
    dispatch({
      type: "PROPS_CHANGED",
      providerRepository: providerRepository || null,
      parsedRepositoryUrl,
    });
  }, [providerRepository, parsedRepositoryUrl]);

  useEffect(() => {
    dispatch({
      type: "SERVICES_CHANGED",
      services: services || null,
      defaultServiceUuid: (hasPreselectedPublicTemplate ? "git" : defaultServiceUuid) || null,
      disableAutoSelect,
    });
  }, [services, defaultServiceUuid, disableAutoSelect]);

  // Prefill form fields when default repository is auto-selected
  useEffect(() => {
    if (state.selectedRepository && !state.hasUserSelection) {
      prefillFormFields({
        name: state.selectedRepository.full_name.split("/").pop() || "",
        description: state.selectedRepository.description,
        defaultBranch: state.selectedRepository.default_branch,
      });
    }
  }, [state.selectedRepository, state.hasUserSelection, prefillFormFields]);

  // Get selected service (computed value)
  const selectedService = useMemo(
    () =>
      (state.selectedServiceUuid &&
        services?.find((service) => service.uuid === state.selectedServiceUuid)) ||
      null,
    [state.selectedServiceUuid, services],
  );

  // Keep track of whether we're updating selected repository changes from turbo powered
  // rendering vs user interaction. We emit an event when user changes the repository but
  // we don't want to emit events when turbo is setting the repository (i.e. when
  // rendering validation errors after submission)
  const prevRepositoryNameRef = useRef<string | null>(null);
  useEffect(() => {
    const currentRepoName = state.selectedRepository?.full_name || null;
    const repositoryActuallyChanged = currentRepoName !== prevRepositoryNameRef.current;

    // Only emit events for user-initiated selections that actually changed
    if (state.hasUserSelection && repositoryActuallyChanged) {
      const repositoryChangeEvent = new CustomEvent<RepositorySelectionEventDetail>(
        "pipeline:new:repositorySelected",
        {
          detail: { repository: state.selectedRepository, scmService: selectedService ?? null },
          bubbles: true,
        },
      );
      document.dispatchEvent(repositoryChangeEvent);
    }

    prevRepositoryNameRef.current = currentRepoName;
  }, [state.selectedRepository, state.hasUserSelection, selectedService]);

  const handleServiceChange = useCallback(
    (service: SCMServiceInfo | null, manualProviderType?: ManualProviderType) => {
      dispatch({ type: "USER_SELECTED_SERVICE", service, manualProviderType });
    },
    [],
  );

  const handleRepositorySelect = useCallback(
    (repository: RepositoryInfo | null) => {
      dispatch({ type: "USER_SELECTED_REPOSITORY", repository });

      if (repository) {
        prefillFormFields({
          name: repository.full_name.split("/").pop() || "",
          description: repository.description,
          defaultBranch: repository.default_branch,
        });
      }
    },
    [prefillFormFields],
  );

  const handleAutoSelectRepository = useCallback(
    (repositories: RepositoryInfo[]) => {
      // Only auto-select if we don't have a current selection and have repositories
      // and auto-select is not disabled
      if (state.selectedRepository || !repositories.length || disableAutoSelect) {
        return;
      }

      const repository =
        (repositoryValue &&
          state.selectedServiceUuid === "examples" &&
          repositories.find((repo) => repo.clone_url === repositoryValue)) ||
        repositories[0];

      dispatch({ type: "USER_AUTO_SELECTED_REPOSITORY", repository });

      prefillFormFields({
        name: repository.full_name.split("/").pop() || "",
        description: repository.description,
        defaultBranch: repository.default_branch,
      });
    },
    [state.selectedRepository, prefillFormFields, disableAutoSelect],
  );

  const handleSwitchToManualEntry = useCallback(() => {
    dispatch({ type: "USER_SELECTED_SERVICE", service: null });
  }, []);

  const handleRepositoryURLTypeChange = useCallback(
    (urlType: RepositoryURLType) => {
      dispatch({ type: "USER_CHANGED_URL_TYPE", urlType });

      state.selectedRepository &&
        prefillFormFields({
          name: state.selectedRepository.full_name.split("/").pop() || "",
          description: state.selectedRepository.description,
          defaultBranch: state.selectedRepository.default_branch,
        });
    },
    [state.selectedRepository, prefillFormFields],
  );

  const handleRepositoryUrlChange = useCallback(
    (url: string) => {
      // Use Rails-parsed data if available, otherwise extract locally
      const repoName = parsedRepositoryUrl?.name || url.split("/").pop()?.replace(".git", "") || "";

      // Use Rails-detected provider if available
      const detectedProvider =
        parsedRepositoryUrl?.detected_provider || state.selectedManualProvider;
      if (detectedProvider && detectedProvider !== state.selectedManualProvider) {
        dispatch({ type: "USER_CHANGED_MANUAL_PROVIDER", provider: detectedProvider });
      }

      prefillFormFields({
        name: repoName,
        description: "",
        defaultBranch: "main",
      });
    },
    [parsedRepositoryUrl, state.selectedManualProvider, prefillFormFields],
  );

  const toggleRepositoryPicker = ({
    serviceUuid,
    open,
  }: {
    serviceUuid?: string;
    open?: boolean;
  }) => {
    dispatch({
      type: "TOGGLE_REPOSITORY_PICKER",
      serviceUuid,
      open,
    });
  };

  return (
    <QueryClientProvider client={queryClient}>
      <div className="flex flex-col pt-2">
        <div className="mb-2 grid w-full grid-flow-col grid-cols-1 grid-rows-4 content-center items-center gap-x-1 gap-y-1 px-3 sm:grid-cols-[minmax(0,_200px)_min-content_minmax(min-content,_50%)_minmax(min-content,_50%)] sm:grid-rows-2">
          <button
            className="col-start-2 row-start-1 self-center justify-self-end font-medium text-purple-600 sm:col-start-4"
            onClick={() => toggleRepositoryPicker({ serviceUuid: "examples" })}
            type="button"
          >
            Or try an example
          </button>

          <label className="m-0 w-fit font-semibold" htmlFor="account-select">
            Git scope
          </label>
          <div className="col-span-2 sm:col-span-1">
            <GitProviderPicker
              services={services}
              selectedService={selectedService}
              selectedManualProvider={state.selectedManualProvider}
              onServiceChange={handleServiceChange}
              connectAccountURL={connectAccountURL}
              connectGitHubAccountURL={connectGitHubAccountURL}
            />
          </div>
          <Icon
            icon="custom/outline/20x/slash"
            className="row-start-2 mt-1.5 hidden h-4 w-4 flex-shrink-0 text-gray-800 sm:block"
          />
          <label className="m-0 font-semibold" htmlFor="repository-select">
            Repository
          </label>
          <div className="col-span-2">
            <RepositoryPicker
              selectedService={selectedService}
              selectedRepository={state.selectedRepository}
              onRepositorySelect={handleRepositorySelect}
              onAutoSelectRepository={handleAutoSelectRepository}
              onRepositoryUrlChange={handleRepositoryUrlChange}
              selectedManualProvider={state.selectedManualProvider}
              repositoryValue={
                selectedService || hasPreselectedPublicTemplate ? repositoryValue : null
              }
              onSwitchToManualEntry={handleSwitchToManualEntry}
              comboboxOpen={state.repositoryPickerOpen}
              setComboboxOpen={(open) => toggleRepositoryPicker({ open })}
            />
          </div>
        </div>
        <p className="dark-gray m-0 px-3 pb-4">
          This is the repository your agents will checkout whenever running this pipeline.
        </p>

        {state.selectedRepository && selectedService && (
          <div className="-mt-2 flex flex-col gap-1 bg-slate-100 px-3 py-2 pb-4">
            <span className="text-sm font-medium">Checkout using</span>
            <div className="flex items-center gap-2">
              <ToolbarButtonGroup className="inline-flex w-fit gap-0.5 rounded-sm border border-gray-500 bg-white p-0.5">
                <ToolbarButton
                  type="button"
                  isActive={state.selectedRepositoryURLType === URLType.SSH}
                  onClick={() => handleRepositoryURLTypeChange(URLType.SSH)}
                  className="text-charcoal-700 focus:text-charcoal-700 flex w-fit cursor-pointer items-center rounded-sm bg-transparent !px-1.5 py-1 text-sm font-medium hover:text-purple-600 hover:no-underline focus:outline-none aria-[checked=true]:bg-purple-100 aria-[checked=true]:!text-purple-600"
                >
                  SSH
                </ToolbarButton>
                <ToolbarButton
                  type="button"
                  isActive={state.selectedRepositoryURLType === URLType.HTTPS}
                  onClick={() => handleRepositoryURLTypeChange(URLType.HTTPS)}
                  className="text-charcoal-700 focus:text-charcoal-700 flex w-fit cursor-pointer items-center rounded-sm bg-transparent !px-1.5 py-1 text-sm font-medium hover:text-purple-600 hover:no-underline focus:outline-none aria-[checked=true]:bg-purple-100 aria-[checked=true]:!text-purple-600"
                >
                  HTTPS
                </ToolbarButton>
              </ToolbarButtonGroup>
              <input
                type="text"
                className="input"
                disabled={true}
                value={state.selectedRepository[state.selectedRepositoryURLType]}
                readOnly={true}
              />
              <input
                type="text"
                className="input"
                readOnly={true}
                value={state.selectedRepository[state.selectedRepositoryURLType]}
                hidden={true}
                id="project_repository"
                name="project[repository]"
              />
            </div>
          </div>
        )}

        {allowWebhooksCheckbox &&
          ((window.Features.SkipNewPipelinesRepositoriesAutocomplete && selectedService?.uuid) ||
            state.selectedRepository) &&
          selectedService?.uuid !== "examples" && (
            <div
              className="group/field flex flex-col gap-2 bg-slate-100 px-3 pb-3 pt-2"
              aria-checked={state.autoCreateWebhooks}
            >
              <div className="form-group flex flex-row items-center gap-1">
                <input
                  name="auto_create_webhooks"
                  id="auto_create_webhooks"
                  type="checkbox"
                  value="true"
                  checked={state.autoCreateWebhooks}
                  onChange={(event) =>
                    dispatch({ type: "USER_CHANGED_WEBHOOKS", enabled: event.target.checked })
                  }
                />
                <label className="text-sm font-medium" htmlFor="auto_create_webhooks">
                  Trigger builds when...
                </label>
              </div>

              <div className="flex flex-row items-center gap-2 opacity-40 group-aria-checked/field:opacity-100">
                <div className="text-charcoal-700 flex items-center gap-1 rounded-md border border-slate-500 bg-white/80 px-2 py-1">
                  <Icon icon="heroicons/16/solid/arrow-up" className="h-4 w-4 text-gray-800" />
                  Code is pushed to a branch
                </div>
                <div className="text-charcoal-700 flex items-center gap-1 rounded-md border border-slate-500 bg-white/80 px-2 py-1">
                  <Icon icon="custom/solid/16x/pull-request" className="h-4 w-4 text-gray-800" />
                  Pull Request is opened
                </div>
              </div>
            </div>
          )}
      </div>
    </QueryClientProvider>
  );
};

export default ConnectGitPicker;
